import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { UserOutlined } from "@ant-design/icons";
import { Avatar, Descriptions, Result, Spin, Typography } from "antd";

import { api } from "../api";
import { formatDate } from "../utils/date";
import { getGravatarUrl } from "../utils/gravatar";
import { usePageTitle } from "../utils/usePageTitle";

import "./PublicProfilePage.css";

import type { PublicProfileData } from "../api/profile";

export const PublicProfilePage = () => {
  const { t, i18n } = useTranslation();
  usePageTitle(t("publicProfile.title"));
  const { guid } = useParams<{ guid: string }>();

  const [profile, setProfile] = useState<PublicProfileData | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!guid) return;
    api.profile
      .getPublicProfile(guid)
      .then((data) => {
        setProfile(data);
        return getGravatarUrl(data.email, 150);
      })
      .then(setAvatarUrl)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [guid]);

  if (loading) {
    return (
      <div className="public-profile__spinner">
        <Spin size="large" />
      </div>
    );
  }

  if (notFound || !profile) {
    return <Result status="404" title={t("publicProfile.notFound")} />;
  }

  const items = [
    {
      key: "username",
      label: t("publicProfile.username"),
      children: profile.username,
    },
    ...(profile.date_joined
      ? [
          {
            key: "date_joined",
            label: t("publicProfile.joinedOn"),
            children: formatDate(profile.date_joined, i18n.language),
          },
        ]
      : []),
    ...(profile.instance_url
      ? [
          {
            key: "webgis",
            label: t("publicProfile.webgis"),
            children: (
              <a
                href={`https://${profile.instance_url}`}
                target="_blank"
                rel="noreferrer"
              >
                {profile.instance_url}
              </a>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="public-profile">
      <Typography.Title level={2}>{t("publicProfile.title")}</Typography.Title>
      <div className="public-profile__body">
        <div className="public-profile__avatar">
          <Avatar
            shape="square"
            size={150}
            icon={<UserOutlined />}
            src={avatarUrl ?? undefined}
          />
        </div>
        <Descriptions column={1} items={items} />
      </div>
    </div>
  );
};
